import { DECK, RANKS, SUITS, analyzeTakingOptions } from "./engine.js";
import { createObserver } from "./observer.js";
import { createPracticeTable } from "./practice.js";
import { initI18n, number, suitName, t } from "./i18n.js";
import { initInstall } from "./install.js";
import { DEMO_DEAL, enterDemo, exitDemo, isDemoMode, resetDemo } from "./runtime-context.js";

initI18n();

const state = {
  hand: [],
  center: null,
  mode: "hand",
  position: null,
  running: false,
  lastAnalysis: null,
};

const elements = {
  deckPicker: document.querySelector("#deck-picker"),
  handCount: document.querySelector("#hand-count"),
  centerStatus: document.querySelector("#center-status"),
  autoStatus: document.querySelector("#auto-status"),
  resetButton: document.querySelector("#reset-button"),
  precision: document.querySelector("#precision"),
  progressWrap: document.querySelector("#progress-wrap"),
  progressValue: document.querySelector("#progress-value"),
  progressBar: document.querySelector("#progress-bar"),
  results: document.querySelector("#results"),
  verdictTitle: document.querySelector("#verdict-title"),
  simulationBadge: document.querySelector("#simulation-badge"),
  recommendation: document.querySelector("#recommendation"),
  returnedResult: document.querySelector("#returned-result"),
  alternativeResult: document.querySelector("#alternative-result"),
  allSuits: document.querySelector("#all-suits"),
  confidenceNote: document.querySelector("#confidence-note"),
};

function suitInfo(suitId) {
  const suit = SUITS.find((item) => item.id === suitId);
  return suit ? { ...suit, name: suitName(suit.id) } : null;
}

function cardById(id) {
  return DECK.find((card) => card.id === id);
}

function renderDeck() {
  elements.deckPicker.innerHTML = SUITS.map(
    (suit) => `
      <div class="suit-row">
        <div class="suit-label ${suit.red ? "red" : ""}">
          <span class="symbol">${suit.symbol}</span><span>${suitName(suit.id)}</span>
        </div>
        ${RANKS.map((rank) => {
          const card = cardById(`${suit.id}-${rank}`);
          const inHand = state.hand.some((item) => item.id === card.id);
          const isCenter = state.center?.id === card.id;
          const selected = inHand || isCenter;
          const modeFull = state.mode === "hand" ? state.hand.length >= 5 : Boolean(state.center);
          const disabled = state.running || (!selected && modeFull);
          return `
            <button
              class="card-button ${suit.red ? "red" : ""} ${selected ? "is-selected" : ""} ${isCenter ? "is-center" : ""}"
              data-card="${card.id}"
              type="button"
              aria-label="${t("app.cardLabel", { rank, suit: suitName(suit.id) })}"
              aria-pressed="${selected}"
              ${disabled ? "disabled" : ""}
            >
              <span class="card-rank">${rank}</span>
              <span class="card-suit-small">${suit.symbol}</span>
            </button>`;
        }).join("")}
      </div>`,
  ).join("");
}

function renderSelectionStatus() {
  elements.handCount.textContent = `${state.hand.length} / 5`;
  if (state.center) {
    const suit = suitInfo(state.center.suit);
    elements.centerStatus.textContent = `${state.center.rank}${suit.symbol}`;
  } else {
    elements.centerStatus.textContent = t("common.none");
  }
}

function setMode(mode) {
  state.mode = mode;
  document.querySelectorAll(".mode-button").forEach((button) => {
    button.classList.toggle("is-active", button.dataset.mode === mode);
  });
  renderDeck();
}

function renderReadiness() {
  const missing = 5 - state.hand.length + (state.center ? 0 : 1);
  elements.autoStatus.textContent = state.running
    ? t("app.running")
    : missing
      ? t("app.missing", { count: missing })
      : !state.position
        ? t("app.choosePlace")
        : t("app.ready");
  elements.resetButton.disabled = state.running;
  document.querySelectorAll(".mode-button, .position-button").forEach((button) => {
    button.disabled = state.running;
  });
}

function render() {
  renderSelectionStatus();
  renderDeck();
  renderReadiness();
}

function selectCard(card) {
  if (state.running) return;
  state.lastAnalysis = null;
  elements.results.hidden = true;
  const handIndex = state.hand.findIndex((item) => item.id === card.id);
  if (handIndex >= 0) {
    state.hand.splice(handIndex, 1);
    setMode("hand");
    render();
    return;
  }
  if (state.center?.id === card.id) {
    state.center = null;
    setMode("center");
    render();
    return;
  }

  if (state.mode === "hand" && state.hand.length < 5) {
    state.hand.push(card);
    if (state.hand.length === 5 && !state.center) setMode("center");
  } else if (state.mode === "center" && !state.center) {
    state.center = card;
    if (state.hand.length < 5) setMode("hand");
  }
  render();
  maybeAutoCalculate();
}

function maybeAutoCalculate() {
  if (!state.running && state.hand.length === 5 && state.center && state.position) {
    void calculate();
  }
}

function reset() {
  if (state.running) return;
  state.hand = [];
  state.center = null;
  state.mode = "hand";
  state.position = null;
  state.lastAnalysis = null;
  document.querySelectorAll(".position-button").forEach((button) => {
    button.classList.remove("is-active");
    button.setAttribute("aria-checked", "false");
  });
  elements.results.hidden = true;
  elements.progressWrap.hidden = true;
  setMode("hand");
  render();
}

function rounded(value, digits = 0) {
  return number(Number(value), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function suitChip(suit) {
  return `<span class="suit-chip ${suit.red ? "red" : ""}">${suit.symbol} ${suit.name}</span>`;
}

function resultCardMarkup(title, result) {
  const suit = suitInfo(result.suit);
  const probability = rounded(result.probability);
  const personal = Number.isFinite(result.personalizedProbability)
    ? `<span class="personal-probability">${t("app.personalProfile", { value: rounded(result.personalizedProbability) })}</span>`
    : "";
  return `
    <div class="result-eyebrow"><span>${title}</span>${suitChip(suit)}</div>
    <div class="probability-line"><span class="probability">${probability}</span><span class="percent-sign">%</span></div>
    <div class="meter" aria-label="${t("common.percent", { value: probability })}"><span style="--value: ${result.probability}%"></span></div>
    <div class="result-meta">
      <span>${t("common.points", { value: `<strong>${rounded(result.expectedPoints, 1)}</strong>` })}</span>
      <span>${t("common.tricks", { value: `<strong>${rounded(result.expectedTricks, 1)}</strong>` })}</span>
      <span>${t("common.margin", { value: `<strong>${rounded(result.ci95, 1)}</strong>` })}</span>
    </div>
    ${personal}`;
}

function effectiveProbability(result) {
  return Number.isFinite(result.personalizedProbability)
    ? result.personalizedProbability
    : result.probability;
}

function recommendationMarkup(analysis) {
  const returned = analysis.returned;
  const alternative = analysis.bestAlternative;
  const returnedSuit = suitInfo(returned.suit);
  const alternativeSuit = suitInfo(alternative.suit);
  const returnedProbability = effectiveProbability(returned);
  const alternativeProbability = effectiveProbability(alternative);
  const personalized = Number.isFinite(returned.personalizedProbability);
  const alternateWins = alternativeProbability > returnedProbability + 1;
  const best = alternateWins ? alternative : returned;
  const bestSuit = alternateWins ? alternativeSuit : returnedSuit;
  const risky = effectiveProbability(best) < 50;

  let title;
  let copy;
  if (alternateWins) {
    title = t("app.recommend.alternativeTitle", { symbol: alternativeSuit.symbol, suit: alternativeSuit.name });
    copy = t("app.recommend.alternativeCopy", { alternative: rounded(alternativeProbability), returned: rounded(returnedProbability) });
  } else {
    title = t("app.recommend.returnedTitle");
    copy = t("app.recommend.returnedCopy", { symbol: returnedSuit.symbol, suit: returnedSuit.name, returned: rounded(returnedProbability) });
  }
  if (risky) copy += t("app.recommend.risky");
  if (personalized) copy += t("app.recommend.personal");

  return `
    <span class="recommendation-icon">${bestSuit.symbol}</span>
    <div><h3>${title}</h3><p>${copy}</p></div>`;
}

function renderResults(analysis) {
  const returnedSuit = suitInfo(analysis.returned.suit);
  const bestSuit = suitInfo(analysis.bestAlternative.suit);
  const effective = Math.min(
    analysis.returned.effectiveSamples,
    analysis.bestAlternative.effectiveSamples,
  );

  elements.simulationBadge.textContent = t("app.simulations", { count: number(analysis.iterations) });
  elements.verdictTitle.textContent = t("app.verdict", { position: analysis.position });
  elements.recommendation.classList.toggle(
    "is-cautious",
    Math.max(effectiveProbability(analysis.returned), effectiveProbability(analysis.bestAlternative)) < 50,
  );
  elements.recommendation.innerHTML = recommendationMarkup(analysis);

  elements.returnedResult.dataset.suit = returnedSuit.symbol;
  elements.returnedResult.innerHTML = resultCardMarkup(t("app.result.returned"), analysis.returned);
  elements.alternativeResult.dataset.suit = bestSuit.symbol;
  elements.alternativeResult.innerHTML = resultCardMarkup(t("app.result.alternative"), analysis.bestAlternative);

  const ordered = [analysis.returned, ...analysis.alternatives];
  elements.allSuits.innerHTML = `
    <div class="all-suits-title">${t("app.comparison")}</div>
    ${ordered
      .map((result) => {
        const suit = suitInfo(result.suit);
        const isReturn = result.suit === analysis.returned.suit;
        return `
          <div class="suit-comparison">
            <span class="suit-name ${suit.red ? "red" : ""}">${suit.symbol} ${suit.name}${isReturn ? t("app.returnedSuffix") : ""}</span>
            <span class="comparison-track"><span style="width:${result.probability}%"></span></span>
            <span class="comparison-value">${rounded(result.probability)} %${Number.isFinite(result.personalizedProbability) ? `<small class="comparison-personal">${t("app.personalSuffix", { value: rounded(result.personalizedProbability) })}</small>` : ""}</span>
          </div>`;
      })
      .join("")}`;
  elements.confidenceNote.textContent = t("app.confidence", { count: number(Math.round(effective)) });
  elements.results.hidden = false;
}

async function calculate() {
  if (state.running || state.hand.length !== 5 || !state.center || !state.position) return;
  state.running = true;
  elements.results.hidden = true;
  elements.progressWrap.hidden = false;
  elements.precision.disabled = true;
  elements.progressBar.style.width = "0%";
  elements.progressValue.textContent = "0 %";
  render();

  try {
    const analysis = await analyzeTakingOptions({
      hand: state.hand,
      center: state.center,
      position: state.position,
      iterations: Number(elements.precision.value),
      onProgress(progress) {
        const percentage = Math.round(progress * 100);
        elements.progressBar.style.width = `${percentage}%`;
        elements.progressValue.textContent = `${percentage} %`;
      },
    });
    const inputSnapshot = {
      hand: [...state.hand],
      center: state.center,
      position: state.position,
    };
    observer.decorateAnalysis(analysis, inputSnapshot);
    state.lastAnalysis = analysis;
    renderResults(analysis);
    void observer.recordAnalysis(inputSnapshot, analysis).catch(() => {});
    elements.results.scrollIntoView({ behavior: "smooth", block: "start" });
  } catch (error) {
    window.alert(t(error?.code ? `app.error.${error.code}` : "common.error"));
  } finally {
    state.running = false;
    elements.precision.disabled = false;
    elements.progressWrap.hidden = true;
    render();
  }
}

elements.deckPicker.addEventListener("click", (event) => {
  const button = event.target.closest("[data-card]");
  if (button) selectCard(cardById(button.dataset.card));
});

document.querySelectorAll(".mode-button").forEach((button) => {
  button.addEventListener("click", () => {
    if (!state.running) setMode(button.dataset.mode);
  });
});

document.querySelectorAll(".position-button").forEach((button) => {
  button.addEventListener("click", () => {
    if (state.running) return;
    state.position = button.dataset.position;
    state.lastAnalysis = null;
    elements.results.hidden = true;
    document.querySelectorAll(".position-button").forEach((item) => {
      const active = item === button;
      item.classList.toggle("is-active", active);
      item.setAttribute("aria-checked", String(active));
    });
    renderReadiness();
    maybeAutoCalculate();
  });
});

elements.resetButton.addEventListener("click", reset);
elements.precision.addEventListener("change", () => {
  state.lastAnalysis = null;
  elements.results.hidden = true;
  maybeAutoCalculate();
});

const observer = createObserver({
  stateProvider: () => ({
    hand: [...state.hand],
    center: state.center,
    position: state.position,
    lastAnalysis: state.lastAnalysis,
  }),
});

const practice = createPracticeTable({
  onDealComplete: (deal) => observer.recordPracticeDeal(deal),
});

function applyRuntimeMode() {
  const demo = isDemoMode();
  document.querySelector("#demo-banner").hidden = !demo;
  document.querySelector("#demo-launch").hidden = demo;
  if (demo) {
    state.hand = DEMO_DEAL.hand.map(cardById);
    state.center = cardById(DEMO_DEAL.center);
    state.position = DEMO_DEAL.position;
    state.mode = "center";
    document.querySelectorAll(".position-button").forEach((button) => {
      const active = button.dataset.position === state.position;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-checked", String(active));
    });
    setTimeout(() => maybeAutoCalculate(), 0);
  }
  document.querySelector("#demo-launch")?.addEventListener("click", enterDemo);
  document.querySelector("#demo-reset")?.addEventListener("click", () => void resetDemo());
  document.querySelector("#demo-exit")?.addEventListener("click", exitDemo);
  if (new URLSearchParams(location.search).get("practice") === "1") practice?.open?.();
}

document.querySelector("#app-version").textContent = t("footer.version", __APP_RELEASE__);
window.addEventListener("app-locale-changed", () => {
  render();
  if (state.lastAnalysis) renderResults(state.lastAnalysis);
  document.querySelector("#app-version").textContent = t("footer.version", __APP_RELEASE__);
});
initInstall();
applyRuntimeMode();
render();
